import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/shared/lib/utils";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import type { UserListItem } from "./types";
import { formatDateShort } from "./format-user";
import { UserRoleBadge } from "./UserRoleBadge";
import { UserStatusBadge } from "./UserStatusBadge";

interface UsersTableProps {
  readonly items: readonly UserListItem[];
}

export function UsersTable({ items }: UsersTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      data-testid="users-table"
      className="overflow-x-auto rounded-lg border border-border bg-card"
    >
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/30 text-left">
          <tr>
            <Th>{t("users.table.user")}</Th>
            <Th>{t("users.table.role")}</Th>
            <Th>{t("users.table.status")}</Th>
            <Th>{t("users.table.created")}</Th>
            <Th>{t("users.table.lastSignIn")}</Th>
            <Th className="w-12">
              <span className="sr-only">{t("users.table.openDetail")}</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {items.map((user) => (
            <tr
              key={user.id}
              data-testid={`user-row-${user.id}`}
              onClick={() => navigate(`/users/${user.id}`)}
              className="cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/40"
            >
              <Td>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-sans font-medium text-foreground">
                      {user.displayName || user.email}
                    </span>
                    <TestPartitionBadge
                      isTest={user.isTestAccount}
                      testId={`user-test-badge-${user.id}`}
                    />
                  </div>
                  <span className="font-sans text-xs text-muted-foreground">{user.email}</span>
                </div>
              </Td>
              <Td>
                <UserRoleBadge role={user.role} />
              </Td>
              <Td>
                <UserStatusBadge isActive={user.isActive} />
              </Td>
              <Td className="text-muted-foreground">{formatDateShort(user.createdAt) || "—"}</Td>
              <Td className="text-muted-foreground">{formatDateShort(user.lastSignInAt) || "—"}</Td>
              <Td className="text-right">
                <ChevronRight
                  className="ml-auto h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "px-4 py-3 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return <td className={cn("px-4 py-3 align-middle", className)}>{children}</td>;
}
